import React, { useState } from 'react';
import { SearchForm, DailySearchForm as DailySearchFormType, UserSearchForm } from '../types';
import { usePeriodForm } from '../hooks/usePeriodForm';
import { useDailyForm } from '../hooks/useDailyForm';
import { useUserSearch } from '../hooks/useUserSearch';
import { PeriodSearchForm } from './PeriodSearchForm';
import { DailySearchForm } from './DailySearchForm';
import { UserSearchFormComponent } from './UserSearchForm';

type SidebarProps = {
  onPeriodSearch: (form: SearchForm) => void;
  onDailySearch: (form: DailySearchFormType) => void;
  onUserSearch: (form: UserSearchForm) => void;
  isLoading: boolean;
  initialPeriodForm?: SearchForm;
  initialDailyForm?: DailySearchFormType;
  initialUserSearchForm?: UserSearchForm;
};

export const Sidebar: React.FC<SidebarProps> = ({ 
  onPeriodSearch, 
  onDailySearch,
  onUserSearch, 
  isLoading, 
  initialPeriodForm, 
  initialDailyForm,
  initialUserSearchForm 
}) => {
  const periodFormLogic = usePeriodForm(initialPeriodForm);
  const dailyFormLogic = useDailyForm(initialDailyForm);
  const userSearchLogic = useUserSearch({ initialForm: initialUserSearchForm });
  const [activeTab, setActiveTab] = useState<'period' | 'daily' | 'user'>('period');

  const handlePeriodSearch = () => {
    if (periodFormLogic.validate()) {
      onPeriodSearch(periodFormLogic.form);
    }
  };

  const handleDailySearch = () => {
    if (dailyFormLogic.validate()) {
      onDailySearch(dailyFormLogic.form);
    }
  };

  const handleUserSearch = () => {
    // バリデーションはフォーム側で実施済み
    onUserSearch(userSearchLogic.formData);
  };

  return (
    <div className="no-print w-80 bg-white shadow-lg border-r border-gray-200 h-full flex flex-col">
      <div className="p-6 border-b border-gray-200">
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          Sleep Coin Analytics
        </h1>
        <p className="text-sm text-gray-600">
          睡眠データを可視化・分析
        </p>
      </div>

      {/* タブナビゲーション */}
      <div className="border-b border-gray-200">
        <div className="flex">
          <button
            onClick={() => setActiveTab('period')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'period'
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            期間検索
          </button>
          <button
            onClick={() => setActiveTab('daily')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'daily'
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            日別検索
          </button>
          <button
            onClick={() => setActiveTab('user')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'user'
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            ユーザー検索
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* 指定期間を表示セクション */}
        {activeTab === 'period' && (
          <PeriodSearchForm
            form={periodFormLogic.form}
            errors={periodFormLogic.errors}
            isLoading={isLoading}
            onUserIdChange={periodFormLogic.handleUserIdChange}
            onStartDateChange={periodFormLogic.handleStartDateChange}
            onEndDateChange={periodFormLogic.handleEndDateChange}
            onSearch={handlePeriodSearch}
          />
        )}

        {/* 指定日を表示セクション */}
        {activeTab === 'daily' && (
          <DailySearchForm
            form={dailyFormLogic.form}
            errors={dailyFormLogic.errors}
            isLoading={isLoading}
            onUserIdChange={dailyFormLogic.handleUserIdChange}
            onSelectedDateChange={dailyFormLogic.handleSelectedDateChange}
            onSearch={handleDailySearch}
          />
        )}

        {/* ユーザー検索セクション */}
        {activeTab === 'user' && (
          <div className="p-6">
            <UserSearchFormComponent
              formData={userSearchLogic.formData}
              onChange={userSearchLogic.handleFieldChange}
              onSubmit={handleUserSearch}
              isLoading={isLoading}
              error={userSearchLogic.error}
            />
          </div>
        )}
      </div>
    </div>
  );
};